import React, { useMemo } from 'react';
import {
  Modal,
  Button,
  MaskedInput,
  Input,
  NumberInput,
  Radio,
  RadioGroup,
  Select,
  TextArea,
} from '@quality24/design-system';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { getValidation } from '@quality24/react-order-components';
import {
  ServiceCustomFieldDataNumber,
  ServiceCustomFieldDataText,
} from '@quality24/inpatient-typings';
import { QueryServiceCustomField } from '@/inpatient-patient-pwa/types/global';
import { validateCPF } from '@/inpatient-patient-pwa/utils/cpfValidation';
import useI18nResolvers from '@/inpatient-patient-pwa/i18n/useI18nResolvers';

export interface Props {
  className?: string;
  show: boolean;
  serviceCustomFields: QueryServiceCustomField[];
  initialValues?: Record<string, string>;
  reasonName?: string;
  onHide: () => void;
  onComplete: (values: Record<string, string>) => void;
}

const PatientCallCustomFieldsModal: React.FunctionComponent<Props> = ({
  className,
  show,
  serviceCustomFields,
  initialValues,
  reasonName,
  onHide,
  onComplete,
}) => {
  const { t } = useTranslation();
  const { resolveI18n } = useI18nResolvers();
  const validationSchema = useMemo(
    () =>
      Yup.object().shape(
        serviceCustomFields.reduce((schema, field) => {
          let fieldSchema = getValidation(field);

          // Add custom validation based on data.validate
          if (
            field.inputType === 'TEXT' &&
            field.data &&
            'validate' in field.data &&
            (field.data as ServiceCustomFieldDataText).validate === 'cpf'
          ) {
            fieldSchema = (fieldSchema as Yup.StringSchema).test(
              'cpf',
              t('validation.cpfInvalid'),
              (value) => {
                if (!value) return true; // Let required validation handle empty values
                return validateCPF(value);
              },
            );
          }

          return {
            ...schema,
            [field.id]: fieldSchema,
          };
        }, {}),
      ),
    [serviceCustomFields, t],
  );

  const formik = useFormik<Record<string, string>>({
    initialValues: initialValues || {},
    validationSchema,
    validateOnMount: false, // Don't validate on mount to avoid showing errors before user interaction
    validateOnChange: true,
    onSubmit: onComplete,
  });

  return (
    <Modal
      id="order-custom-fields-modal"
      className={className}
      show={show}
      onHide={onHide}
      title={reasonName || t('order.createRequest')}
    >
      <div className="d-flex flex-column gap-3 mb-3 pb-2">
        {serviceCustomFields.map((field) => {
          // Check if field has maskPattern to use MaskedInput
          const textData =
            field.inputType === 'TEXT' && field.data
              ? (field.data as ServiceCustomFieldDataText)
              : null;

          if (textData && textData.maskPattern) {
            return (
              <MaskedInput
                key={field.id}
                name={field.id}
                label={field.labelI18N}
                placeholder={field.placeholderI18N || undefined}
                mask={textData.maskPattern}
                replacement={
                  textData.replacement
                    ? Object.fromEntries(
                        Object.entries(textData.replacement).flatMap(
                          ([k, v]) => {
                            try {
                              return [[k, new RegExp(v)]];
                            } catch {
                              return [];
                            }
                          },
                        ),
                      )
                    : undefined
                }
                value={formik.values[field.id] || ''}
                errorText={formik.errors[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.value);
                }}
                inputMode="numeric"
              />
            );
          }

          // Render other field types
          if (
            field.inputType === 'TEXT' &&
            field.data &&
            'asTextArea' in field.data &&
            (field.data as { asTextArea?: boolean }).asTextArea
          ) {
            const textAreaData = field.data as ServiceCustomFieldDataText;
            return (
              <TextArea
                key={field.id}
                name={field.id}
                label={field.labelI18N}
                placeholder={field.placeholderI18N || undefined}
                value={formik.values[field.id] || ''}
                maxLength={textAreaData.maxLength || 512}
                errorText={formik.errors[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.value);
                }}
              />
            );
          }

          if (field.inputType === 'TEXT') {
            return (
              <Input
                key={field.id}
                name={field.id}
                label={field.labelI18N}
                placeholder={field.placeholderI18N || undefined}
                value={formik.values[field.id] || ''}
                maxLength={
                  'maxLength' in field.data && field.data.maxLength
                    ? field.data.maxLength
                    : 256
                }
                errorText={formik.errors[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.value);
                }}
              />
            );
          }

          if (field.inputType === 'NUMBER') {
            return (
              <NumberInput
                key={field.id}
                name={field.id}
                label={field.labelI18N}
                placeholder={field.placeholderI18N || undefined}
                value={formik.values[field.id] || ''}
                errorText={formik.errors[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.value);
                }}
                allowsDecimal={
                  (field.data as ServiceCustomFieldDataNumber).allowsDecimal
                }
                decimalPrecision={
                  (field.data as ServiceCustomFieldDataNumber).decimalPrecision
                }
              />
            );
          }

          if (field.inputType === 'DATE') {
            return (
              <Input
                key={field.id}
                name={field.id}
                type="date"
                label={field.labelI18N}
                placeholder={field.placeholderI18N || undefined}
                value={formik.values[field.id] || ''}
                errorText={formik.errors[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.value);
                }}
              />
            );
          }

          if (field.inputType === 'TIME') {
            return (
              <Input
                key={field.id}
                name={field.id}
                type="time"
                label={field.labelI18N}
                placeholder={field.placeholderI18N || undefined}
                value={formik.values[field.id] || ''}
                errorText={formik.errors[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.value);
                }}
              />
            );
          }

          if (
            field.inputType === 'MULTIPLE_CHOICE' &&
            'choices' in field.data &&
            (!field.data.displayMode || field.data.displayMode === 'radio') &&
            field.data.choices.length > 0
          ) {
            return (
              <RadioGroup
                key={field.id}
                label={field.labelI18N}
                cols={2}
                errorText={formik.errors[field.id]}
              >
                {field.data.choices.map((choice) => (
                  <Radio
                    key={choice.id}
                    name={field.id}
                    label={
                      resolveI18n(choice.labelI18N) || choice.label || choice.id
                    }
                    value={choice.id}
                    checked={formik.values[field.id] === choice.id}
                    onBlur={() => formik.setFieldTouched(field.id)}
                    onChange={(e) => {
                      formik.setFieldValue(field.id, e.target.value);
                    }}
                  />
                ))}
              </RadioGroup>
            );
          }

          if (
            field.inputType === 'MULTIPLE_CHOICE' &&
            'choices' in field.data &&
            field.data.displayMode === 'select' &&
            field.data.choices.length > 0
          ) {
            return (
              <Select
                key={field.id}
                type="select"
                className="w-100"
                name={field.id}
                label={field.labelI18N}
                placeholder={field.placeholderI18N || t('order.selectOption')}
                options={field.data.choices.map((choice) => ({
                  id: choice.id,
                  label:
                    resolveI18n(choice.labelI18N) || choice.label || choice.id,
                }))}
                value={formik.values[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(newValue) => {
                  formik.setFieldValue(field.id, (newValue as string) || '');
                }}
                errorText={formik.errors[field.id]}
              />
            );
          }

          if (field.inputType === 'EMAIL') {
            return (
              <Input
                key={field.id}
                name={field.id}
                type="email"
                label={field.labelI18N}
                placeholder={field.placeholderI18N || undefined}
                value={formik.values[field.id] || ''}
                errorText={formik.errors[field.id]}
                onBlur={() => formik.setFieldTouched(field.id)}
                onChange={(e) => {
                  formik.setFieldValue(field.id, e.target.value);
                }}
              />
            );
          }

          return null;
        })}
      </div>

      <div className="d-flex flex-row flex-justify-end gap-2">
        <Button
          fluid
          size="lg"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || serviceCustomFields.length === 0}
          debounceInterval={500}
        >
          {t('order.openCall')}
        </Button>
      </div>
    </Modal>
  );
};

export default PatientCallCustomFieldsModal;
